export interface PendingPfPolicy {
    policyName?: string;
    employeeContributionPercent?: number;
    employerContributionPercent?: number;
    maxWageLimit?: number;
    isVoluntary?: boolean;
    status?: string; // active | inactive
}

export interface PendingDeletePfPolicy {
    awaitingName?: boolean;
}

export const startCreatePfPolicyFlow = (
    setPending: (data: PendingPfPolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({ policyName: nameFromIntent });

        pushAIChat("What is the employee contribution percentage?");
        speak("What is the employee contribution percentage?");
        return;
    }

    setPending({});
    pushAIChat("What should the PF policy be called?");
    speak("What should the PF policy be called?");
};

export const processPendingPfPolicy = (
    text: string,
    pending: PendingPfPolicy,
    setPending: (data: PendingPfPolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — POLICY NAME
    if (!pending.policyName) {
        const name = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!name) {
            pushAIChat("Please provide a valid policy name.");
            speak("Please provide a valid policy name.");
            return;
        }

        setPending({ ...pending, policyName: name });

        pushAIChat("What is the employee contribution percentage?");
        speak("What is the employee contribution percentage?");
        return;
    }

    // STEP 2 — EMPLOYEE CONTRIBUTION
    if (!pending.employeeContributionPercent) {
        const value = Number(text.replace(/[^0-9.]/g, ""));

        if (!value || value <= 0) {
            pushAIChat("Please provide a valid employee contribution percentage.");
            speak("Invalid employee contribution percentage.");
            return;
        }

        setPending({ ...pending, employeeContributionPercent: value });

        pushAIChat("What is the employer contribution percentage?");
        speak("What is the employer contribution percentage?");
        return;
    }

    // STEP 3 — EMPLOYER CONTRIBUTION
    if (!pending.employerContributionPercent) {
        const value = Number(text.replace(/[^0-9.]/g, ""));

        if (!value || value <= 0) {
            pushAIChat("Please provide a valid employer contribution percentage.");
            speak("Invalid employer contribution percentage.");
            return;
        }

        setPending({ ...pending, employerContributionPercent: value });

        pushAIChat("What is the maximum wage limit? You can say zero if not applicable.");
        speak("What is the maximum wage limit?");
        return;
    }

    // STEP 4 — MAX WAGE LIMIT
    if (pending.maxWageLimit === undefined) {
        const value = Number(text.replace(/[^0-9.]/g, ""));

        if (isNaN(value) || value < 0) {
            pushAIChat("Please provide a valid wage limit.");
            speak("Invalid wage limit.");
            return;
        }

        setPending({ ...pending, maxWageLimit: value });

        pushAIChat("Is this a voluntary PF policy? Yes or No.");
        speak("Is this a voluntary PF policy?");
        return;
    }

    // STEP 5 — VOLUNTARY
    if (pending.isVoluntary === undefined) {
        const isVoluntary = text.toLowerCase().includes("yes");

        setPending({ ...pending, isVoluntary });

        pushAIChat("Should this policy be active or inactive?");
        speak("Should this policy be active or inactive?");
        return;
    }

    // STEP 6 — STATUS
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";

        setPending({ ...pending, status });

        // FINAL — CREATE PF POLICY
        pushAIChat(`Creating PF policy "${pending.policyName}"...`);
        speak(`Creating PF policy ${pending.policyName}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-pf-policy", {
                detail: {
                    ...pending,
                    status
                }
            })
        );

        setPending(null);
        return;
    }
};

export const startDeletePfPolicyFlow = (
    setPending: (data: PendingDeletePfPolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting PF policy "${nameFromIntent}"...`);
        speak(`Deleting PF policy ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-pf-policy", {
                detail: { policyName: nameFromIntent }
            })
        );
        return;
    }

    setPending({ awaitingName: true });
    pushAIChat("Which PF policy would you like to delete?");
    speak("Which PF policy would you like to delete?");
};

export const processPendingDeletePfPolicy = (
    text: string,
    pending: PendingDeletePfPolicy,
    setPending: (data: PendingDeletePfPolicy | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const name = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

    if (!name) {
        pushAIChat("Please provide a valid PF policy name.");
        speak("Please provide a valid PF policy name.");
        return;
    }

    pushAIChat(`Deleting PF policy "${name}"...`);
    speak(`Deleting PF policy ${name}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-pf-policy", {
            detail: { policyName: name }
        })
    );

    setPending(null);
};
